import React, { useState } from 'react';
import {
  Box,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Badge,
  Button,
  Text,
  MailIcon,
  TableToolbar,
  Heading,
  ArrowUpRightIcon,
  RefreshIcon,
} from '@razorpay/blade/components';
import { usePagination } from 'hooks/usePagination';
import { useMissingInformationReport } from './hooks/useMissingInformationReport';
import { getTableToolbarText } from './utils';
import { useHistory, useLocation } from 'react-router-dom';
import { useSendReminder } from './hooks/useSendReminder';
import { SendReminderModal } from './SendReminderModal';
import { prepareUrlV2 } from 'utils/Urls';
import { employeeOnboardingSteps, routePaths } from 'components/Routes/data';
import { MissingStatutoryContribution } from 'api/types/missingInformationReportTypes';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import useFeature from 'hooks/useFeature';
import { ExportReportButton } from './components/ExportReportButton';

const PanValueBadge = ({
  value,
  status,
}: {
  status?: MissingStatutoryContribution['pan_status'];
  value?: MissingStatutoryContribution['pan'];
}) => {
  if (value && status === 'invalid') {
    return <Badge color="negative">Invalid</Badge>;
  }
  if (value && status === 'valid') {
    return <Text color="surface.text.gray.subtle">{value}</Text>;
  }
  if (value && !status) {
    return <Badge color="negative">Unverified</Badge>;
  }
  return <Badge color="negative">Missing</Badge>;
};

const PfEsicValueBadge = ({ isEnabled, value }: { isEnabled: boolean; value: string | null }) => {
  if (!isEnabled) {
    return <Badge color="neutral">Not Applicable</Badge>;
  }
  if (!value) {
    return <Badge color="negative">Missing</Badge>;
  }
  return <Text color="surface.text.gray.subtle">{value}</Text>;
};

export const StatutoryContributions = () => {
  const [selectedTableRowIds, setSelectedTableRowIds] = useState<(string | number)[]>([]);
  const { isBulkOnboardingFlagEnabled } = useFeature('bulkOnboardingFlag');
  const {
    isSendReminderModalOpen,
    remindEmployeeIds,
    onOpenModal,
    onCloseModal,
    sendReminder,
    isSendingReminder,
  } = useSendReminder('statutory_contributions');

  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const location = useLocation();
  const history = useHistory();

  const {
    data: employeeData,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useMissingInformationReport('statutory_contributions', currentPage, itemsPerPage);

  const moveToStatutoryForm = (userId: number) => {
    if (isBulkOnboardingFlagEnabled) {
      const url = prepareUrlV2(routePaths.employeeDetailsV2.statutoryContributions.edit, {
        userId: userId,
        redirect: location.pathname,
      });
      history.push(url);
    } else {
      const url = prepareUrlV2(routePaths.phpRoutes.employeeOnboarding, {
        userId: userId,
        step: employeeOnboardingSteps.statutoryContributions,
        redirect: location.pathname,
      });
      window.location.href = url;
    }
  };

  const shouldShowEmptyView =
    !isError && !isLoading && !isFetching && employeeData?.data.items.length === 0;
  const shouldShowErrorView = isError;
  const shouldShowTable = !shouldShowEmptyView && !shouldShowErrorView;

  return (
    <Box>
      <Heading
        size="large"
        weight="semibold"
        color="surface.text.gray.normal"
        marginBottom="spacing.2">
        Statutory Contributions
      </Heading>
      <Text color="surface.text.gray.subtle" marginBottom="spacing.7">
        Remind employees or provide statutory contribution details for compliance
      </Text>
      {isError && <ErrorView onRefetch={refetch} />}
      {shouldShowEmptyView && (
        <EmptyListView
          title="🔍 No Missing Information Found"
          description="Great! All employees have completed their details."
        />
      )}
      {shouldShowTable && (
        <Table
          isLoading={isLoading}
          isRefreshing={isFetching}
          data={{ nodes: employeeData?.data.items || [] }}
          onSelectionChange={({ selectedIds }) => {
            setSelectedTableRowIds(selectedIds);
          }}
          selectionType="multiple"
          multiSelectTrigger="checkbox"
          backgroundColor="surface.background.gray.intense"
          toolbar={
            <TableToolbar
              selectedTitle={`${selectedTableRowIds.length} Employees Selected`}
              title={getTableToolbarText(
                employeeData?.data.total_count || 0,
                currentPage,
                itemsPerPage,
              )}>
              <Box display="flex" justifyContent="flex-end" gap="spacing.5">
                <ExportReportButton type="statutory_contributions" />
                <Button
                  icon={MailIcon}
                  isDisabled={selectedTableRowIds.length === 0}
                  onClick={() => {
                    onOpenModal({
                      remindEmployeeIds: selectedTableRowIds,
                    });
                  }}>
                  Send Reminder
                </Button>
                <Button
                  icon={RefreshIcon}
                  variant="tertiary"
                  accessibilityLabel="Refresh Statutory Contributions"
                  onClick={refetch}
                  isDisabled={isFetching || isLoading}
                />
              </Box>
            </TableToolbar>
          }
          pagination={
            <TablePagination
              paginationType="server"
              showPageSizePicker={true}
              currentPage={currentPage}
              totalItemCount={employeeData?.data.total_count || 0}
              onPageChange={({ page }) => setCurrentPage(page)}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
                setCurrentPage(0);
              }}
            />
          }>
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>PAN</TableHeaderCell>
                  <TableHeaderCell>PF UAN</TableHeaderCell>
                  <TableHeaderCell>ESIC IP Number</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {tableData.map((employee) => (
                  <TableRow
                    key={employee.id}
                    item={employee}
                    hoverActions={
                      <Box display="flex" gap="spacing.4">
                        <Button
                          size="xsmall"
                          variant="tertiary"
                          icon={MailIcon}
                          iconPosition="left"
                          onClick={() => {
                            onOpenModal({
                              remindEmployeeIds: [employee.id],
                            });
                          }}>
                          Send Reminder
                        </Button>
                        <Button
                          iconPosition="left"
                          icon={ArrowUpRightIcon}
                          size="xsmall"
                          variant="secondary"
                          onClick={() => moveToStatutoryForm(employee.id)}>
                          Employee Profile
                        </Button>
                      </Box>
                    }>
                    <TableCell>
                      <Box>
                        <Text color="surface.text.gray.subtle">{employee.name}</Text>
                        <Text size="small" color="surface.text.gray.muted">
                          {employee.employee_id}
                        </Text>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <PanValueBadge value={employee.pan} status={employee.pan_status} />
                    </TableCell>
                    <TableCell>
                      <PfEsicValueBadge
                        isEnabled={employee.is_pf_enabled}
                        value={employee.pf_uan}
                      />
                    </TableCell>
                    <TableCell>
                      <PfEsicValueBadge
                        isEnabled={employee.is_esic_enabled}
                        value={employee.esic_ip}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      )}
      <SendReminderModal
        isOpen={isSendReminderModalOpen}
        onClose={onCloseModal}
        description={
          remindEmployeeIds.length === 1
            ? 'RazorpayX Payroll will email this employee to remind them to fill in their statutory contribution details.'
            : "RazorpayX Payroll will email each selected person in your organization who hasn't provided their statutory contribution details."
        }
        onSendReminder={() => {
          sendReminder({
            employee_ids: remindEmployeeIds.map((id) => Number(id)),
            reminder_type: 'statutory_contributions',
          });
        }}
        isSendingReminder={isSendingReminder}
      />
    </Box>
  );
};
